import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query'

import { api } from './client'
import type {
  AccountUser,
  AuthResponse,
  ExchangeFile,
  ExcludedRecipe,
  FieldOptions,
  FoodLookupStatus,
  FoodSearchResponse,
  ImportPrefs,
  ImportReport,
  Ingredient,
  MealOptions,
  Page,
  PairEvaluation,
  PartnerRecommendations,
  PrepResult,
  Recipe,
  RecipeDetail,
  RecipeFilters,
  RecipeSummary,
  RuleCatalog,
  RulePreset,
  RuleState,
  UserCreated,
  WeightPreset,
  WeightSettings,
  WeightState,
} from './types'
import type { Tuning } from '@/features/recommendations/tuning'

/** Query keys, centralised so invalidation cannot drift from usage. */
export const keys = {
  me: ['auth', 'me'] as const,
  bootstrap: ['auth', 'bootstrap-status'] as const,
  recipes: (filters?: RecipeFilters) => ['recipes', 'list', filters ?? {}] as const,
  recipe: (id: number | string | null) => ['recipes', 'detail', id] as const,
  recipeSearch: (term: string, limit: number) =>
    ['recipes', 'search', { term, limit }] as const,
  excludedRecipes: ['recipes', 'excluded'] as const,
  fieldOptions: ['recipes', 'field-options'] as const,
  ingredients: (params?: Record<string, unknown>) =>
    ['ingredients', 'list', params ?? {}] as const,
  ingredient: (id: number | string) => ['ingredients', 'detail', id] as const,
  foodLookupStatus: ['food-lookup', 'status'] as const,
  foodSearch: (q: string, page: number) =>
    ['food-lookup', 'search', { q, page }] as const,
  mealOptions: (limit: number, tuning: Tuning, rules: string[]) =>
    ['recommendations', 'meal-options', { limit, tuning, rules }] as const,
  partners: (
    meal1Id: number,
    limit: number,
    tuning: Tuning,
    rules: string[],
  ) =>
    ['recommendations', 'partners', { meal1Id, limit, tuning, rules }] as const,
  pairEvaluation: (
    meal1Id: number,
    meal2Id: number,
    tuning: Tuning,
    rules: string[],
  ) =>
    [
      'recommendations',
      'evaluate',
      { meal1Id, meal2Id, tuning, rules },
    ] as const,
  ruleCatalog: ['recommendations', 'rule-catalog'] as const,
  rulePresets: ['rule-presets'] as const,
  ruleState: ['rule-state'] as const,
  weightPresets: ['weight-presets'] as const,
  weightState: ['weight-state'] as const,
  users: ['admin', 'users'] as const,
  exchangeSpec: ['exchange', 'spec'] as const,
  // Under 'ingredients' so every ingredient mutation refreshes it too.
  ingredientCatalog: ['ingredients', 'catalog'] as const,
  importPrefs: ['import-prefs'] as const,
}

// ----- session -----

export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: ({ signal }) => api.get<AccountUser>('/auth/me', undefined, signal),
    retry: false,
    staleTime: 60_000,
  })
}

export function useBootstrapStatus() {
  return useQuery({
    queryKey: keys.bootstrap,
    queryFn: ({ signal }) =>
      api.get<{ needs_bootstrap: boolean }>(
        '/auth/bootstrap-status',
        undefined,
        signal,
      ),
    retry: false,
    staleTime: 5_000,
  })
}

export function useLogin() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { username: string; password: string }) =>
      api.post<AuthResponse>('/auth/login', input),
    onSuccess: (data) => {
      client.setQueryData(keys.me, data.user)
    },
  })
}

export function useBootstrap() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      username: string
      display_name: string
      password: string
    }) => api.post<AuthResponse>('/auth/bootstrap', input),
    onSuccess: (data) => {
      client.setQueryData(keys.me, data.user)
      void client.invalidateQueries({ queryKey: keys.bootstrap })
    },
  })
}

export function useLogout() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<void>('/auth/logout'),
    onSuccess: () => {
      client.setQueryData(keys.me, null)
      client.clear()
    },
  })
}

export function useChangePassword(): UseMutationResult<
  AuthResponse,
  Error,
  { current_password: string; new_password: string }
> {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { current_password: string; new_password: string }) =>
      api.post<AuthResponse>('/auth/change-password', input),
    onSuccess: (data) => {
      client.setQueryData(keys.me, data.user)
    },
  })
}

// ----- recipes -----

export function useRecipes(filters: RecipeFilters, page: { limit: number; offset: number }) {
  return useQuery({
    // The page is part of the key: two views can ask for the same filters with
    // different windows and must not overwrite each other's cached page.
    queryKey: [...keys.recipes(filters), page],
    queryFn: ({ signal }) =>
      api.get<Page<RecipeSummary>>(
        '/recipes',
        {
          ...filters,
          limit: page.limit,
          offset: page.offset,
        },
        signal,
      ),
    placeholderData: (previous) => previous,
  })
}

export function useRecipe(id: number | string | null) {
  return useQuery({
    queryKey: keys.recipe(id),
    queryFn: ({ signal }) => api.get<RecipeDetail>(`/recipes/${id}`, undefined, signal),
    enabled: id !== null,
  })
}

/**
 * Recipes by name, for a picker.
 *
 * The collection is too large to hold in the browser, so the list is asked for
 * by name and the server does the matching.
 */
export function useRecipeSearch(
  term: string,
  options: { limit?: number; enabled?: boolean } = {},
) {
  const limit = options.limit ?? 25
  return useQuery({
    queryKey: keys.recipeSearch(term, limit),
    queryFn: ({ signal }) =>
      api.get<Page<RecipeSummary>>(
        '/recipes',
        {
          q: term.trim() || undefined,
          excluded: false,
          sort: 'name',
          order: 'asc',
          limit,
          offset: 0,
        },
        signal,
      ),
    staleTime: 30_000,
    placeholderData: (previous) => previous,
    enabled: options.enabled ?? true,
  })
}

/** Full replace: scalars and the ingredient set in one request. */
export function useSaveRecipe() {
  const client = useQueryClient()
  return useMutation({
    // Create answers with the bare recipe, update with the full detail, so
    // normalise both to the id the caller needs to navigate on.
    mutationFn: async ({
      id,
      ...input
    }: {
      id?: number
    } & Record<string, unknown>): Promise<number> => {
      if (id === undefined) {
        const created = await api.post<Recipe>('/recipes', input)
        return created.id
      }
      await api.put<RecipeDetail>(`/recipes/${id}`, input)
      return id
    },
    onSuccess: (id) => {
      void client.invalidateQueries({ queryKey: ['recipes'] })
      void client.invalidateQueries({ queryKey: keys.recipe(id) })
      void client.invalidateQueries({ queryKey: ['recommendations'] })
    },
  })
}

export function useRecipeImage() {
  const client = useQueryClient()
  const invalidate = (id: number) => {
    void client.invalidateQueries({ queryKey: keys.recipe(id) })
    void client.invalidateQueries({ queryKey: ['recipes'] })
  }
  return {
    upload: useMutation({
      mutationFn: ({ id, file }: { id: number; file: File }) =>
        api.upload<RecipeDetail>(`/recipes/${id}/image`, file),
      onSuccess: (detail) => invalidate(detail.recipe.id),
    }),
    remove: useMutation({
      mutationFn: (id: number) => api.delete<RecipeDetail>(`/recipes/${id}/image`),
      onSuccess: (detail) => invalidate(detail.recipe.id),
    }),
  }
}

export function useDeleteRecipe() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/recipes/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['recipes'] })
      void client.invalidateQueries({ queryKey: ['recommendations'] })
    },
  })
}

export function useSetExcluded() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, exclude, reason }: { id: number; exclude: boolean; reason?: string }) =>
      exclude
        ? api.post<RecipeDetail>(`/recipes/${id}/exclude`, { reason: reason ?? null })
        : api.delete<RecipeDetail>(`/recipes/${id}/exclude`),
    onSuccess: (detail) => {
      void client.invalidateQueries({ queryKey: ['recipes'] })
      void client.invalidateQueries({ queryKey: keys.recipe(detail.recipe.id) })
      void client.invalidateQueries({ queryKey: keys.excludedRecipes })
      void client.invalidateQueries({ queryKey: ['recommendations'] })
    },
  })
}

/** Mark meals as cooked. One request for the whole combination. */
export function useRecordPrep() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (recipeIds: number[]) =>
      api.post<PrepResult>('/recipes/prepare', { recipe_ids: recipeIds }),
    onSuccess: (result) => {
      void client.invalidateQueries({ queryKey: ['recipes'] })
      void client.invalidateQueries({ queryKey: ['recommendations'] })
      if (result.changed.length > 0) {
        result.changed.forEach((id) => {
          void client.invalidateQueries({ queryKey: keys.recipe(id) })
        })
      }
    },
  })
}

/** Undo a preparation: drops the most recent date, or the one given. */
export function useRemovePrep() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({
      recipeIds,
      preparedOn,
    }: {
      recipeIds: number[]
      preparedOn?: string
    }) =>
      api.post<PrepResult>('/recipes/unprepare', {
        recipe_ids: recipeIds,
        prepared_on: preparedOn ?? null,
      }),
    onSuccess: (_result, variables) => {
      void client.invalidateQueries({ queryKey: ['recipes'] })
      void client.invalidateQueries({ queryKey: ['recommendations'] })
      variables.recipeIds.forEach((id) => {
        void client.invalidateQueries({ queryKey: keys.recipe(id) })
      })
    },
  })
}

/** Set the whole history at once. This is how the count is corrected by hand. */
export function useSetPreparations() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, dates }: { id: number; dates: string[] }) =>
      api.put<RecipeDetail>(`/recipes/${id}/preparations`, { dates }),
    onSuccess: (detail) => {
      void client.invalidateQueries({ queryKey: ['recipes'] })
      void client.invalidateQueries({ queryKey: keys.recipe(detail.recipe.id) })
      void client.invalidateQueries({ queryKey: ['recommendations'] })
    },
  })
}

export function useExcludedRecipes() {
  return useQuery({
    queryKey: keys.excludedRecipes,
    queryFn: ({ signal }) =>
      api.get<ExcludedRecipe[]>('/recipes/excluded', undefined, signal),
  })
}

export function useFieldOptions() {
  return useQuery({
    queryKey: keys.fieldOptions,
    queryFn: ({ signal }) =>
      api.get<FieldOptions>('/recipes/field-options', undefined, signal),
    staleTime: 5 * 60_000,
  })
}

// ----- ingredients -----

export function useIngredients(params: Record<string, unknown>) {
  return useQuery({
    queryKey: keys.ingredients(params),
    queryFn: ({ signal }) =>
      api.get<Page<Ingredient>>('/ingredients', params, signal),
    placeholderData: (previous) => previous,
  })
}

export function useIngredientSearch(search: string) {
  // The picker needs names only, so keep it small and uncached while typing.
  return useQuery({
    queryKey: keys.ingredients({ q: search, limit: 50 }),
    queryFn: ({ signal }) =>
      api.get<Page<Ingredient>>(
        '/ingredients',
        { q: search, limit: 50, sort: 'name', order: 'asc' },
        signal,
      ),
    staleTime: 30_000,
    placeholderData: (previous) => previous,
  })
}

export function useCreateIngredient() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api.post<Ingredient>('/ingredients', input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['ingredients'] })
    },
  })
}

export function useUpdateIngredient() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...input }: { id: number } & Record<string, unknown>) =>
      api.patch<Ingredient>(`/ingredients/${id}`, input),
    onSuccess: (updated) => {
      void client.invalidateQueries({ queryKey: ['ingredients'] })
      void client.invalidateQueries({ queryKey: keys.ingredient(updated.id) })
      void client.invalidateQueries({ queryKey: ['recipes'] })
    },
  })
}

export function useDeleteIngredient() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/ingredients/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['ingredients'] })
      void client.invalidateQueries({ queryKey: ['recipes'] })
    },
  })
}

// ----- food lookup -----

/**
 * Whether this server can look ingredients up at all.
 *
 * A missing key is a stable answer, not a transient failure, so it is never
 * retried and never refetched: the deployment does not gain a key while a tab
 * is open.
 */
export function useFoodLookupStatus() {
  return useQuery({
    queryKey: keys.foodLookupStatus,
    queryFn: ({ signal }) =>
      api.get<FoodLookupStatus>('/food-lookup/status', undefined, signal),
    retry: false,
    staleTime: Infinity,
  })
}

/**
 * Search FoodData Central. The key allows 1,000 requests an hour, so nothing
 * fires until there are two characters to search for.
 */
export function useFoodSearch(q: string, page = 1) {
  const term = q.trim()
  return useQuery({
    queryKey: keys.foodSearch(term, page),
    queryFn: ({ signal }) =>
      api.get<FoodSearchResponse>('/food-lookup/search', { q: term, page }, signal),
    enabled: term.length >= 2 && page >= 1,
    staleTime: 5 * 60_000,
    placeholderData: (previous) => previous,
  })
}

// ----- recommendations -----
//
// A request asks for as many results as the user has paged through. Keeping the
// previous page while the next one loads means a "show more" does not blank the
// list that is already on screen.

export function useMealOptions(
  limit: number,
  tuning: Tuning,
  rules: string[],
  enabled = true,
) {
  return useQuery({
    queryKey: keys.mealOptions(limit, tuning, rules),
    queryFn: ({ signal }) =>
      api.get<MealOptions>(
        '/recommendations/meal-options',
        { limit, ...tuning, rule: rules },
        signal,
      ),
    enabled,
    placeholderData: (previous) => previous,
  })
}

export function usePartners(
  meal1Id: number | null,
  limit: number,
  tuning: Tuning,
  rules: string[],
  enabled = true,
) {
  return useQuery({
    queryKey: keys.partners(meal1Id ?? 0, limit, tuning, rules),
    queryFn: ({ signal }) =>
      api.get<PartnerRecommendations>(
        '/recommendations/partners',
        { meal1_id: meal1Id, limit, ...tuning, rule: rules },
        signal,
      ),
    enabled: meal1Id !== null && enabled,
    placeholderData: (previous) => previous,
  })
}

export function usePairEvaluation(
  meal1Id: number | null,
  meal2Id: number | null,
  tuning: Tuning,
  rules: string[],
  enabled = true,
) {
  return useQuery({
    queryKey: keys.pairEvaluation(meal1Id ?? 0, meal2Id ?? 0, tuning, rules),
    queryFn: ({ signal }) =>
      api.get<PairEvaluation>(
        '/recommendations/evaluate',
        { meal1_id: meal1Id, meal2_id: meal2Id, ...tuning, rule: rules },
        signal,
      ),
    enabled: meal1Id !== null && meal2Id !== null && enabled,
  })
}

// ----- admin -----

export function useUsers() {
  return useQuery({
    queryKey: keys.users,
    queryFn: ({ signal }) => api.get<AccountUser[]>('/admin/users', undefined, signal),
  })
}

export function useCreateUser() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      username: string
      display_name: string
      role: 'admin' | 'user'
    }) => api.post<UserCreated>('/admin/users', input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.users })
    },
  })
}

export function useUpdateUser() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: {
      id: number
      display_name?: string
      role?: 'admin' | 'user'
      is_active?: boolean
    }) => api.patch<AccountUser>(`/admin/users/${id}`, input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.users })
    },
  })
}

export function useResetUserPassword() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.post<UserCreated>(`/admin/users/${id}/reset-password`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.users })
    },
  })
}

export function useDeleteUser() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/admin/users/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.users })
    },
  })
}
// ----- rules -----
//
// The catalogue changes only when the backend does, so it is fetched once and
// kept. Everything the rule editor renders comes from it.

export function useRuleCatalog() {
  return useQuery({
    queryKey: keys.ruleCatalog,
    queryFn: ({ signal }) =>
      api.get<RuleCatalog>('/recommendations/rule-catalog', undefined, signal),
    staleTime: 60 * 60_000,
  })
}

export function useRulePresets() {
  return useQuery({
    queryKey: keys.rulePresets,
    queryFn: ({ signal }) => api.get<RulePreset[]>('/rule-presets', undefined, signal),
  })
}

export function useSaveRulePreset() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: {
      id?: number
      name: string
      rules: string[]
    }) =>
      payload.id === undefined
        ? api.post<RulePreset>('/rule-presets', payload)
        : api.put<RulePreset>(`/rule-presets/${payload.id}`, payload),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: keys.rulePresets }),
  })
}

export function useDeleteRulePreset() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete(`/rule-presets/${id}`),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: keys.rulePresets }),
  })
}

export function useRuleState() {
  return useQuery({
    queryKey: keys.ruleState,
    queryFn: ({ signal }) => api.get<RuleState>('/rule-state', undefined, signal),
  })
}

export function useSaveRuleState() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { rules: string[] }) =>
      api.put<RuleState>('/rule-state', payload),
    onSuccess: (state) => queryClient.setQueryData(keys.ruleState, state),
  })
}

// ----- weights -----
//
// One saved or remembered set applies to the whole workflow.

export function useWeightState() {
  return useQuery({
    queryKey: keys.weightState,
    queryFn: ({ signal }) =>
      api.get<WeightState>('/weight-state', undefined, signal),
  })
}

export function useSaveWeightState() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { weights: WeightSettings }) =>
      api.put<WeightState>('/weight-state', payload),
    onSuccess: (state) => queryClient.setQueryData(keys.weightState, state),
  })
}

export function useWeightPresets() {
  return useQuery({
    queryKey: keys.weightPresets,
    queryFn: ({ signal }) =>
      api.get<WeightPreset[]>('/weight-presets', undefined, signal),
  })
}

export function useSaveWeightPreset() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: {
      id?: number
      name: string
      weights: WeightSettings
    }) =>
      payload.id === undefined
        ? api.post<WeightPreset>('/weight-presets', payload)
        : api.put<WeightPreset>(`/weight-presets/${payload.id}`, payload),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: keys.weightPresets }),
  })
}

export function useDeleteWeightPreset() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/weight-presets/${id}`),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: keys.weightPresets }),
  })
}

// ----- recipe exchange -----

/** The import format as markdown, for pasting into an LLM chat. */
export function useExchangeSpec() {
  return useQuery({
    queryKey: keys.exchangeSpec,
    queryFn: () => api.get<string>('/exchange/spec'),
    staleTime: Infinity,
  })
}

/** Match a file against the library. A mutation because it is a POST; it writes nothing. */
export function useResolveImport() {
  return useMutation({
    mutationFn: (file: unknown) => api.post<ImportReport>('/exchange/resolve', file),
  })
}

/** The catalog as the LLM sees it. Cached here because the import page shows its size. */
export function useIngredientCatalog() {
  return useQuery({
    queryKey: keys.ingredientCatalog,
    queryFn: ({ signal }) => api.get<ExchangeFile>('/exchange/ingredients', undefined, signal),
  })
}

export function useImportPrefs() {
  return useQuery({
    queryKey: keys.importPrefs,
    queryFn: ({ signal }) => api.get<ImportPrefs>('/import-prefs', undefined, signal),
  })
}

export function useSaveImportPrefs() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (prefs: ImportPrefs) => api.put<ImportPrefs>('/import-prefs', prefs),
    onSuccess: (prefs) => queryClient.setQueryData(keys.importPrefs, prefs),
  })
}

/** Exports are one-off downloads, so plain fetches rather than cached queries. */
export function fetchIngredientCatalog(): Promise<ExchangeFile> {
  return api.get<ExchangeFile>('/exchange/ingredients')
}

export function fetchRecipeExport(ids?: number[]): Promise<ExchangeFile> {
  return api.get<ExchangeFile>('/exchange/recipes', ids ? { id: ids } : undefined)
}
